import { useQuery } from "@tanstack/react-query";
import isEqual from "lodash/isEqual";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import {
  setTransactionFilters,
  setTransactionSearchValue,
} from "@/redux/slices/transactionSlice";
import type { EnumPaymentType } from "@/api/cashier";
import { getTransactionsOptions } from "@/api/cashier/@tanstack/react-query.gen.ts";
import { ErrorPage } from "@/components/error-page/ErrorPage.tsx";
import { SearchInput } from "@/components/search-input/SearchInput.tsx";
import { useTradingAccounts } from "@/hooks/useTradingAccounts.ts";
import { MOBILE_BREAKPOINT } from "@/utils/constants.ts";
import { ECashierFlows } from "@/utils/enums.ts";
import { getAssetUrl } from "@/utils/helpers.ts";
import { TransactionExportButton } from "./TransactionExportButton.tsx";
import styles from "./Transactions.module.css";
import type { SelectedValuesType } from "@/components/transaction/transaction-history-table-filter/types";
import { useStaticOptions } from "@/components/transaction/transaction-history-table-filter/hooks/useStaticOptions";
import { TransactionHistoryTable } from "@/components/transaction/transaction-history-table/TransactionHistoryTable";
import { useDynamicOptions } from "@/components/transaction/transaction-history-table-filter/hooks/useDynamicOptions";
import { useFilteredTransactions } from "@/components/transaction/transaction-history-table-filter/hooks/useFilteredTransactions";
import TransactionHistoryTableFilter from "@/components/transaction/transaction-history-table-filter/TransactionHistoryTableFilter";
import TransactionHistoryFilterDrawer from "@/components/transaction-history-table-filter-drawer/TransactionHistoryFilterDrawer.tsx";

export const Transactions = () => {
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    };
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const [isMobile, setIsMobile] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const storedFilters = useAppSelector(
    (state) => state.transaction.transactionFilters
  );
  const storedSearchValue = useAppSelector(
    (state) => state.transaction.transactionSearchValue
  );
  const {
    data: transactions,
    isLoading: isTransactionsLoading,
    isError: isTransactionsError,
    refetch: transactionsRefetch,
  } = useQuery(getTransactionsOptions());
  const {
    data: accounts,
    isLoading: isAccountsLoading,
    error: isAccountError,
  } = useTradingAccounts();

  const isLoading = isTransactionsLoading || isAccountsLoading;
  const isError = isTransactionsError || isAccountError;

  const [showFilterDrawer, setShowFilterDrawer] = useState(false);

  const { transactionOptions, timeOptions, statusOptions } = useStaticOptions();
  const { accountOptions, paymentMethodOptions, currencyOptions } =
    useDynamicOptions({
      accounts: accounts ?? [],
      transactions: transactions ?? [],
    });

  const defaultSelectedValues = useMemo<SelectedValuesType>(
    () => ({
      transaction: transactionOptions[0],
      time: timeOptions[0],
      status: statusOptions[0],
      paymentMethod: {
        label: t("transactions.history.filterOptions.all"),
        value: "all",
      },
      account:
        accountOptions.length === 1
          ? accountOptions[0]
          : {
              label: t("transactions.history.filterOptions.all"),
              value: "all",
            },
      currency:
        currencyOptions.length === 1
          ? currencyOptions[0]
          : {
              label: t("transactions.history.filterOptions.all"),
              value: "all",
            },
    }),
    [
      transactionOptions,
      timeOptions,
      statusOptions,
      accountOptions,
      currencyOptions,
      t,
    ]
  );

  const selectedFilterValues: SelectedValuesType = storedFilters
    ? {
        time:
          timeOptions.find((o) => o.value === storedFilters.time) ??
          defaultSelectedValues.time,
        transaction:
          transactionOptions.find(
            (o) => o.value === storedFilters.transaction
          ) ?? defaultSelectedValues.transaction,
        status:
          statusOptions.find((o) => o.value === storedFilters.status) ??
          defaultSelectedValues.status,
        paymentMethod:
          paymentMethodOptions.find(
            (o) => o.value === storedFilters.paymentMethod
          ) ?? defaultSelectedValues.paymentMethod,
        account:
          accountOptions.find((o) => o.value === storedFilters.account) ??
          defaultSelectedValues.account,
        currency:
          currencyOptions.find((o) => o.value === storedFilters.currency) ??
          defaultSelectedValues.currency,
      }
    : defaultSelectedValues;
  const searchValue = storedSearchValue;

  const setSelectedFilterValues = useCallback(
    (filters: SelectedValuesType) => {
      dispatch(
        setTransactionFilters({
          time: filters.time.value,
          transaction: filters.transaction.value,
          status: filters.status.value,
          paymentMethod: filters.paymentMethod.value,
          account: filters.account.value,
          currency: filters.currency.value,
        })
      );
    },
    [dispatch]
  );

  const filteredTransactionList = useFilteredTransactions({
    transactions: transactions ?? [],
    selectedFilterValues,
  }).filter((tx) =>
    tx.reference_id?.toLowerCase().includes(searchValue.trim().toLowerCase())
  );

  const handleReset = useCallback(() => {
    dispatch(setTransactionFilters(null));
    dispatch(setTransactionSearchValue(""));
  }, [dispatch]);

  const handleNoHistoryEvent = useCallback(() => {
    navigate(`/${ECashierFlows.Deposit}`);
  }, [navigate]);

  const onViewDetails = useCallback(
    (type: EnumPaymentType, referenceId: string) => {
      navigate(`/${ECashierFlows.History}/${type}/${referenceId}`);
    },
    [navigate]
  );

  const icon = getAssetUrl("filter.svg", "icons");

  if (isError) {
    return (
      <ErrorPage
        showButton={true}
        title={t("transactions.error.title")}
        message={t("transactions.error.text")}
        buttonLabel={t("transactions.error.button_text")}
        buttonAction={transactionsRefetch}
      />
    );
  }

  if (!isLoading && !transactions?.length) {
    return (
      <ErrorPage
        showButton={true}
        title={t("transactions.no_history.title")}
        message={t("transactions.no_history.text")}
        buttonLabel={t("transactions.no_history.button_text")}
        buttonAction={handleNoHistoryEvent}
      />
    );
  }

  return (
    <div className={styles.section}>
      <div className={styles.transactionTitleBar}>
        {isMobile ? (
          <>
            <div className={styles.mobileTitleRow}>
              <h1 className={styles.title}>
                {t("transactions.history.transaction_history_mobile")}
              </h1>
              <TransactionExportButton
                exportData={filteredTransactionList}
                tradingAccounts={accounts ?? []}
                isLoading={isLoading}
              />
            </div>
            <div className={styles.mobileSearchAndFilter}>
              <SearchInput
                value={searchValue}
                onChange={(v) => dispatch(setTransactionSearchValue(v))}
              />
              <button
                type="button"
                onClick={() => setShowFilterDrawer(true)}
                className={styles.filterIconBtn}
                aria-label="Open filter options"
              >
                <img src={icon} alt={""} />
              </button>
            </div>
          </>
        ) : (
          <>
            <div className={styles.desktopTitleRow}>
              <h1 className={styles.title}>
                {t("transactions.history.transaction_history")}
              </h1>
              <SearchInput
                value={searchValue}
                onChange={(v) => dispatch(setTransactionSearchValue(v))}
              />
              <TransactionExportButton
                exportData={filteredTransactionList}
                tradingAccounts={accounts ?? []}
                isLoading={isLoading}
              />
            </div>
            <TransactionHistoryTableFilter
              isLoading={isLoading}
              selectedValues={selectedFilterValues}
              paymentMethodOptions={paymentMethodOptions}
              accountOptions={accountOptions}
              currencyOptions={currencyOptions}
              timeOptions={timeOptions}
              statusOptions={statusOptions}
              transactionOptions={transactionOptions}
              setSelectedValues={setSelectedFilterValues}
              btnIsDisabled={isEqual(
                selectedFilterValues,
                defaultSelectedValues
              )}
              handleReset={handleReset}
            />
          </>
        )}
      </div>
      <TransactionHistoryTable
        isMobileView={isMobile}
        data={filteredTransactionList}
        tradingAccounts={accounts ?? []}
        isLoading={isLoading}
        onViewDetails={onViewDetails}
        handleNoResultEvent={handleReset}
      />
      {showFilterDrawer && (
        <TransactionHistoryFilterDrawer
          isLoading={isLoading}
          selectedValues={selectedFilterValues}
          paymentMethodOptions={paymentMethodOptions}
          accountOptions={accountOptions}
          currencyOptions={currencyOptions}
          timeOptions={timeOptions}
          statusOptions={statusOptions}
          transactionOptions={transactionOptions}
          setSelectedValues={setSelectedFilterValues}
          btnIsDisabled={isEqual(selectedFilterValues, defaultSelectedValues)}
          handleReset={handleReset}
          onClose={() => setShowFilterDrawer(false)}
          defaultValues={defaultSelectedValues}
        />
      )}
    </div>
  );
};
